import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { useFormikContext } from 'formik';
import { appConfig } from 'brand-config';

import useSession from 'core/hooks/use-session';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import useStyles from './coverage-limit-override.styles';

/**
 * Coverage A (dwelling) / Coverage E (scheduled property) limit override (issue #35617).
 *
 * A Descope-authority control that LOCKS a coverage limit against edits by anyone without the
 * authority, with a required internal reason. When a 360Value estimate comes in high, an authorized
 * user (canOverrideCoverageLimit) corrects the limit, checks this box to protect it, and records why.
 * Non-authority users never see the checkbox; they see the limit read-only with a "reach out to
 * underwriting" note (the forms disable the field via `isCoverageLimitLockedForUser`).
 *
 * Turning the override ON stamps who/when. It deliberately leaves the sibling "Adjust dwelling/scheduled
 * property limit" toggle (canEditCoverageAmount) alone (#35868): the override has its own validation
 * bypass (`limitOverride` in yup-helpers — min $1,000, no max) and the backend preserves a locked value
 * regardless of the toggle, so auto-enabling it only made the adjust description a second required
 * reason for one change. Turning it OFF (authority only) records who/when it was removed and lets the
 * normal limit rules resume; the reason and the corrected limit value are left intact for the audit trail.
 *
 * Gated by the `canOverrideCoverageLimit` permission AND the brand `featureFlags.coverageLimitOverride`
 * flag (on for the mutual brands — CFM, Forreston, Oakwood; off for Branch).
 */
const CoverageLimitOverride = ({ fieldName, coverageLabel, coverages, disabled }) => {
  const classes = useStyles();
  const { canOverrideCoverageLimit, user } = useSession();
  const { setFieldValue } = useFormikContext();

  const coverageLimitOverrideEnabled = Boolean(appConfig?.featureFlags?.coverageLimitOverride);

  // Non-authority users (or brands without the flag) never see the control at all — the limit stays
  // read-only for them, enforced by the consuming form via isCoverageLimitLockedForUser.
  if (!(canOverrideCoverageLimit && coverageLimitOverrideEnabled)) {
    return null;
  }

  const overridePath = `${fieldName}.limitOverride`;
  const reasonPath = `${fieldName}.limitOverrideReason`;

  const isOverridden = Boolean(coverages?.limitOverride);
  const overrideBy = coverages?.limitOverrideBy;

  const handleToggle = (_fieldNameOrChecked, maybeChecked) => {
    // The switch field-type calls onChange(name, checked) in the app, but the test stub calls
    // onChange(checked) with a single boolean — accept whichever argument is the boolean.
    const isOn = typeof maybeChecked === 'boolean' ? maybeChecked : Boolean(_fieldNameOrChecked);
    if (isOn) {
      setFieldValue(overridePath, true);
      setFieldValue(`${fieldName}.limitOverrideBy`, user?.email ?? null);
      setFieldValue(`${fieldName}.limitOverrideAt`, new Date().toISOString());
      // Clear any prior removal audit stamp — this is now an active override.
      setFieldValue(`${fieldName}.limitOverrideRemovedBy`, null);
      setFieldValue(`${fieldName}.limitOverrideRemovedAt`, null);
    } else {
      setFieldValue(overridePath, false);
      setFieldValue(`${fieldName}.limitOverrideRemovedBy`, user?.email ?? null);
      setFieldValue(`${fieldName}.limitOverrideRemovedAt`, new Date().toISOString());
      // Leave the limit value, canEditCoverageAmount, and limitOverrideReason as-is: normal rules
      // resume and the reason stays for the audit trail.
    }
  };

  return (
    <Grid item xs={12} className={classes.container}>
      <Field
        id={overridePath}
        name={overridePath}
        type="switch"
        mode="dark"
        label={`Override ${coverageLabel} limit (locks the limit)`}
        tooltipText={`Locks the ${coverageLabel} limit against edits by anyone without override authority. A reason is required.`}
        tooltipLabel="More Info"
        xs={8}
        disabled={disabled}
        onChange={handleToggle}
      />
      {isOverridden && (
        <>
          <Grid item xs={12} className={classes.reasonWrap}>
            <Field
              id={reasonPath}
              name={reasonPath}
              type="textarea"
              mode="dark"
              label="Reason for override (required, internal only)"
              xs={12}
              rows={3}
              disabled={disabled}
            />
          </Grid>
          <Label type="infoLabel" className={classes.note}>
            {`Overridden by ${overrideBy || 'authorized user'}`}
          </Label>
        </>
      )}
    </Grid>
  );
};

CoverageLimitOverride.propTypes = {
  fieldName: PropTypes.string.isRequired,
  coverageLabel: PropTypes.oneOf(['Coverage A', 'Coverage E']).isRequired,
  coverages: PropTypes.object,
  disabled: PropTypes.bool
};

CoverageLimitOverride.defaultProps = {
  coverages: null,
  disabled: false
};

/**
 * "Locked for this user" predicate consumed by the coverage forms to disable the limit field (and its
 * A-derived dependents) for a non-authority user once an authorized user has locked the limit. Returns
 * true only when the brand flag is on, the coverages carry an active override, and the current user
 * lacks the override authority.
 */
export const isCoverageLimitLockedForUser = ({ coverages, canOverrideCoverageLimit, coverageLimitOverrideEnabled }) =>
  Boolean(coverageLimitOverrideEnabled && coverages?.limitOverride && !canOverrideCoverageLimit);

export default CoverageLimitOverride;
